import React from 'react';
import styled, { useTheme } from 'styled-components';
import Table from 'react-bootstrap/Table';

import Text from './Text';
import Box from './layout/Box';
import BookImage from './BookImage';
import { useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import Icon from './Icon';
import { DateTime } from 'luxon';

const StyledBox = styled(Box)`
  flex-direction: column;
  align-items: center;
  flex-grow: 1;

  @media (min-width: calc(769px + ${({ leftNavbarOpen }) =>
      leftNavbarOpen ? 300 : 0}px)) {
    flex-direction: row;
    align-items: flex-start;
  }
`;

const StyledImageBox = styled(Box)`
  width: 100%;
  min-width: 0;
  justify-content: center;
  background-color: #e2e2e2; // ${({ theme }) => theme.colors['whiteColor']};

  @media (min-width: calc(769px + ${({ leftNavbarOpen }) =>
      leftNavbarOpen ? 300 : 0}px)) {
    width: auto;
    flex-shrink: 0;

    background-color: ${({ theme }) => theme.colors['transparentColor']};
  }
`;

const StyledTableBox = styled(Box)`
  width: 100%;

  @media (min-width: calc(769px + ${({ leftNavbarOpen }) =>
      leftNavbarOpen ? 300 : 0}px)) {
    width: auto;
    padding-left: 10px;
  }
`;

const StyledUserBox = styled(Box)`
  width: 100%;

  @media (min-width: calc(769px + ${({ leftNavbarOpen }) =>
      leftNavbarOpen ? 300 : 0}px)) {
    width: auto;
  }
`;

const BookCard = ({
  id,
  image,
  title,
  author,
  subtitle,
  comment,
  price,
  condition,
  isbn,
  user,
  createdAt,
  isLoggedIn,
}) => {
  const leftNavbarOpen = useSelector(
    (state) => state.navigation.leftNavbarOpen,
  );

  const productData = {
    title,
    subtitle,
    author,
    isbn,
    price: `${price} kr`,
    condition,
    comment,
  };

  const fullTitle = `${title}${subtitle ? ` - ${subtitle}` : ``}`;

  // Table keys are title, subtitle, author, isbn13, isbn10, publisher, pages, edition, language, description
  const tableKeys = [
    {
      key: 'author',
      name: 'Författare',
    },
    {
      key: 'isbn',
      name: 'ISBN13',
    },
    {
      key: 'price',
      name: 'Pris',
    },
    {
      key: 'condition',
      name: 'Skick',
    },
    {
      key: 'comment',
      name: 'Kommentar',
    },
  ];

  const theme = useTheme();

  const { lightBlackColor } = theme.colors;

  const now = DateTime.local();

  const created = DateTime.fromMillis(parseInt(createdAt, 10));

  let dayText = created.setLocale('sv-SE').toRelativeCalendar();
  const dayDiff = now.diff(created, 'days').toObject().days;

  if (dayDiff > 2) {
    dayText = created.setLocale('sv-SE').toFormat('yyyy-MM-dd');
  }

  return (
    <Box className="flex-column ">
      <Box
        className="mb-3 flex-column pb-3"
        style={{
          borderBottom: '2px solid #e7e7e7',
        }}
      >
        <Text variant="sectionTitle">
          <FormattedMessage defaultMessage="Inlägg" />
        </Text>
        <Text variant="sectionSubtitle" className="mt-1">
          <FormattedMessage
            defaultMessage="Det här inlägget skapades {date}"
            values={{ date: dayText }}
          />
        </Text>
      </Box>

      <Box
        className="flex-grow-1 mb-3 pb-3"
        style={{
          borderBottom: '2px solid #e7e7e7',
        }}
      >
        <Box className="flex-row justify-content-between w-100">
          <StyledUserBox
            className="flex-shrink-0"
            style={{
              minWidth: 0,
            }}
          >
            <Box
              className="flex-row flex-grow-1 align-items-center"
              style={{
                minWidth: 300,
              }}
            >
              <Box>
                {isLoggedIn ? (
                  <Box className="flex-column">
                    <Text
                      style={{
                        fontWeight: '600',
                      }}
                    >
                      Säljes av {user?.name}
                    </Text>
                    <Box className="flex-row align-items-center mt-2">
                      {user?.email && (
                        <Box className="align-items-center me-3">
                          <Icon
                            name="email-outline"
                            color={lightBlackColor}
                            size={24}
                          />
                          <Text
                            color="lightBlackColor"
                            className="ms-2"
                            style={{
                              fontSize: 16,
                            }}
                          >
                            {user?.email}
                          </Text>
                        </Box>
                      )}
                      {user?.phone && (
                        <Box className="align-items-center">
                          <Icon
                            name="phone-outline"
                            color={lightBlackColor}
                            size={24}
                          />
                          <Text
                            color="lightBlackColor"
                            className="ms-2"
                            style={{
                              fontSize: 16,
                            }}
                          >
                            {user?.phone}
                          </Text>
                        </Box>
                      )}
                    </Box>
                  </Box>
                ) : (
                  <Box>
                    <Text>Logga in för att kontakta säljare</Text>
                  </Box>
                )}
              </Box>
            </Box>
          </StyledUserBox>
        </Box>
      </Box>

      <StyledBox className="mb-3" leftNavbarOpen={leftNavbarOpen}>
        <StyledImageBox className="mb-3">
          <BookImage bookId={id} source={image} size={150} />
        </StyledImageBox>

        <StyledTableBox className="flex-column flex-grow-1">
          <Text
            className="mb-2"
            style={{
              paddingLeft: 4,
            }}
            variant="cardProductTitle"
          >
            {fullTitle}
          </Text>
          <Table
            borderless
            size="sm"
            style={{
              marginBottom: 0,
            }}
          >
            <tbody>
              {tableKeys.map(({ key, name, formatter }) => {
                let value = productData[key];

                if (key === 'condition') {
                  value = condition?.name;
                }

                if (!value) {
                  return null;
                }

                return (
                  <tr key={key}>
                    <td
                      style={{
                        textAlign: 'left',
                        paddingRight: 10,
                      }}
                    >
                      <Text className="text-muted">{name}</Text>
                    </td>
                    <td style={{ textAlign: 'left' }}>
                      <Text>{formatter ? formatter(value) : value}</Text>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </StyledTableBox>
      </StyledBox>
    </Box>
  );
};

export default BookCard;
